import { isAreaEnabled, getLogger } from './logger';

/**
 * Check if timing logging is enabled.
 */
export function isTimingEnabled(): boolean {
  return isAreaEnabled('timing');
}

/**
 * Format current time as HH:MM:SS.mmm
 */
function timestamp(): string {
  const now = new Date();
  const h = String(now.getHours()).padStart(2, '0');
  const m = String(now.getMinutes()).padStart(2, '0');
  const s = String(now.getSeconds()).padStart(2, '0');
  const ms = String(now.getMilliseconds()).padStart(3, '0');
  return `${h}:${m}:${s}.${ms}`;
}

/**
 * Log a timing entry. No-op when timing is disabled.
 *
 * @param label - The operation label (e.g. "START createModel.POST /models")
 * @param suffix - Optional suffix (e.g. duration, status)
 */
export function logTiming(label: string, suffix?: string): void {
  if (!isAreaEnabled('timing')) return;
  const logger = getLogger('timing');
  const msg = suffix
    ? `[TIMING] ${timestamp()} ${label} ${suffix}`
    : `[TIMING] ${timestamp()} ${label}`;
  logger.info(msg);
}

/**
 * Wrap an async function with START/END timing logs.
 * When timing is disabled, calls fn() directly with no overhead.
 *
 * @param label - The operation label
 * @param fn - The async function to time
 * @returns The result of fn()
 */
export async function timeAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
  if (!isAreaEnabled('timing')) return fn();

  const logger = getLogger('timing');
  const t0 = performance.now();
  logger.info(`[TIMING] ${timestamp()} START ${label}`);

  try {
    const result = await fn();
    const duration = Math.round(performance.now() - t0);
    logger.info(`[TIMING] ${timestamp()}   END ${label} (${duration}ms)`);
    return result;
  } catch (error) {
    const duration = Math.round(performance.now() - t0);
    logger.info(`[TIMING] ${timestamp()}  FAIL ${label} (${duration}ms)`);
    throw error;
  }
}
